import {gql} from '@apollo/client';

import {AaNaturalAnalog} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import staticURL from 'lib/staticURL';
import {CoreLibMonomer} from 'molbio/monomers/MonomersUtil';
import sortedBy from 'util/sortedBy';

export const AA_MONOMER_LIBRARY_URL = '/aa-monomers/listing';

export const AA_HELM_LIBRARY_LINK = staticURL('/static/aa_monomers/HELMCoreLibrary.json');

export const AAHELMCoreMonomerLibraryAPI = {
  fetch: async (): Promise<Array<CoreLibMonomer>> => {
    const resp = await fetch(AA_HELM_LIBRARY_LINK);
    return (await resp.json()) as Array<CoreLibMonomer>;
  },
};

export const AA_MONOMER_QUERY = gql`
  query AaMonomerQuery @skipAudit @engComponent(name: MODIFIED_BIOLOGICS) {
    aaMonomers {
      id
      name
      symbol
      naturalAnalog
      canonicalSmiles
      originalSmiles
      visualColor
      calculatedMolecularWeight
      exactMolecularWeight
      customMolecularWeight
      chemicalStructureImage {
        ... on SVGNode {
          base64Svg
        }
        ... on ErrorNode {
          message
        }
      }
    }
    currentTenant {
      id
      permissions {
        canCreateAaMonomers
      }
    }
  }
`;

export const AA_MONOMER_BY_ID = gql`
  query AaMonomerByIdQuery($id: StrictString!) @skipAudit @engComponent(name: MODIFIED_BIOLOGICS) {
    aaMonomer(id: $id) {
      ... on AaMonomerNode {
        id
        name
        symbol
        naturalAnalog
        isNatural
        canonicalSmiles
        originalSmiles
        visualColor
        calculatedMolecularWeight
        exactMolecularWeight
        customMolecularWeight
        chemicalStructureImage {
          ... on SVGNode {
            base64Svg
          }
          ... on ErrorNode {
            message
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const AA_NATURAL_ANALOG_DISPLAY_NAME: Record<AaNaturalAnalog, string> = {
  [AaNaturalAnalog.A]: 'Alanine',
  [AaNaturalAnalog.B]: 'Aspartic acid or Asparagine',
  [AaNaturalAnalog.C]: 'Cysteine',
  [AaNaturalAnalog.D]: 'Aspartic acid',
  [AaNaturalAnalog.E]: 'Glutamic acid',
  [AaNaturalAnalog.F]: 'Phenylalanine',
  [AaNaturalAnalog.G]: 'Glycine',
  [AaNaturalAnalog.H]: 'Histidine',
  [AaNaturalAnalog.I]: 'Isoleucine',
  [AaNaturalAnalog.J]: 'Leucine or Isoleucine',
  [AaNaturalAnalog.K]: 'Lysine',
  [AaNaturalAnalog.L]: 'Leucine',
  [AaNaturalAnalog.M]: 'Methionine',
  [AaNaturalAnalog.N]: 'Asparagine',
  [AaNaturalAnalog.O]: 'Pyrrolysine',
  [AaNaturalAnalog.P]: 'Proline',
  [AaNaturalAnalog.Q]: 'Glutamine',
  [AaNaturalAnalog.R]: 'Arginine',
  [AaNaturalAnalog.S]: 'Serine',
  [AaNaturalAnalog.T]: 'Threonine',
  [AaNaturalAnalog.U]: 'Selenocysteine',
  [AaNaturalAnalog.V]: 'Valine',
  [AaNaturalAnalog.W]: 'Tryptophan',
  [AaNaturalAnalog.X]: 'Any amino acid',
  [AaNaturalAnalog.Y]: 'Tyrosine',
  [AaNaturalAnalog.Z]: 'Glutamic acid or Glutamine',
};

export const AA_NATURAL_ANALOG_FILTER_OPTIONS = [
  {name: 'Any natural analog', value: [] as Array<AaNaturalAnalog>},
  ...sortedBy(Object.values(AaNaturalAnalog) as Array<AaNaturalAnalog>, [(na) => na]).map((na) => ({
    name: AA_NATURAL_ANALOG_DISPLAY_NAME[na],
    value: [na],
  })),
];
